import React, { useState, useEffect } from 'react';
import { trackCallCtaExit } from '@/lib/analytics';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import SEOHead from '@/components/SEOHead';
import PublicPrivacyLinks from '@/components/privacy/PublicPrivacyLinks';
import useOperatingFlags from '@/hooks/useOperatingFlags';
import { useBusinessHours } from '@/hooks/useBusinessHours';
import { 
  Sparkles, 
  Clock, 
  MapPin, 
  Phone, 
  ChevronRight,
  Star,
  Heart
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Spa-style imagery
const HERO_IMAGE = "https://images.unsplash.com/photo-1703108783854-a3f23dbaab95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcGF8ZW58MHx8fHRlYWx8MTc3MjcxNzAxNHww&ixlib=rb-4.1.0&q=85&w=1920";

// Unique images for each service category
const CATEGORY_IMAGES = {
  'manicure': "https://images.unsplash.com/photo-1632345031435-8727f6897d53?w=800&q=80",
  'pedicure': "https://images.unsplash.com/photo-1519415510236-718bdfcd89c8?w=800&q=80",
  'gel-dip-nexgen': "https://images.unsplash.com/photo-1604654894610-df63bc536371?w=800&q=80",
  'acrylic': "https://images.unsplash.com/photo-1607779097040-26e80aa78e66?w=800&q=80",
  'facial': "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=800&q=80",
  'brow-lash': "https://images.unsplash.com/photo-1562322140-8baeececf3df?w=800&q=80",
  'princess': "https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?w=800&q=80",
  'combinations': "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80"
};

// Category icons and taglines
const CATEGORY_DATA = {
  'manicure': { icon: '💅', tagline: 'Beautiful hands, beautiful you' },
  'pedicure': { icon: '🦶', tagline: 'Relaxation from head to toe' },
  'gel-dip-nexgen': { icon: '✨', tagline: 'Long-lasting perfection' },
  'acrylic': { icon: '💎', tagline: 'Length and strength' },
  'facial': { icon: '🧖‍♀️', tagline: 'Radiant, glowing skin' },
  'brow-lash': { icon: '👁️', tagline: 'Frame your beautiful eyes' },
  'princess': { icon: '👸', tagline: 'Pampering for little ones' },
  'combinations': { icon: '🎁', tagline: 'Save time, save more' }
};

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState(null);
  const [variantReady, setVariantReady] = useState(false);
  const operatingFlags = useOperatingFlags();
  // Default Booking Page CTA mode is INDEPENDENT of the master operating-mode
  // toggle. HomePage is one of the two surfaces that respect this setting
  // (the other is /book). Polled every 30s so flipping the toggle from the
  // portal swaps visible CTAs without a refresh. See
  // /app/backend/routes/landing_variants.py → DEFAULT_PAGE_SETTINGS_KEY.
  const [defaultPageCtaMode, setDefaultPageCtaMode] = useState('online_booking');
  useEffect(() => {
    let cancelled = false;
    const fetchMode = () => {
      fetch(`${BACKEND_URL}/api/landing-variants/default-page-settings`)
        .then(r => r.json())
        .then(d => {
          if (!cancelled && d?.cta_mode) setDefaultPageCtaMode(d.cta_mode);
        })
        .catch(() => {});
    };
    fetchMode();
    const t = setInterval(fetchMode, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const onlineBooking = defaultPageCtaMode === 'online_booking';
  const businessHours = useBusinessHours();

  // Fire a click-to-call tracking beacon (used when we route a "Book Now"
  // click to a tel: link because online booking is currently disabled).
  const trackClickToCall = (channel = 'homepage') => {
    // P1.4B.1 — durable Call CTA-Exit event carrying full acquisition
    // snapshot (R-B1-35/37). trackCallCtaExit internally re-fires the
    // existing click_to_call GAds conversion, so no double GA hit.
    try { trackCallCtaExit(`homepage_${channel}`, { channel, surface: 'homepage' }); } catch {}
    try {
      fetch(`${BACKEND_URL}/api/landing-variants/public/click-to-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variant_id: sessionStorage.getItem('lp_variant_id') || null,
          visitor_id: sessionStorage.getItem('lp_visitor_id') || null,
          campaign_tracking_id: searchParams.get('campaign') || null,
          channel,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  };

  // Build booking URL that preserves campaign/UTM params
  const buildBookUrl = (extra = '') => {
    const params = new URLSearchParams();
    ['campaign', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(k => {
      const v = searchParams.get(k);
      if (v) params.set(k, v);
    });
    const qs = params.toString();
    return `/book${extra}${qs ? (extra.includes('?') ? '&' : '?') + qs : ''}`;
  };

  useEffect(() => {
    fetchCategories();
    // Fetch structured data for JSON-LD
    fetch(`${BACKEND_URL}/api/seo/structured-data`)
      .then(r => r.json())
      .then(data => {
        // Inject JSON-LD via DOM
        let el = document.querySelector('script[data-seo-jsonld]');
        if (!el) {
          el = document.createElement('script');
          el.type = 'application/ld+json';
          el.setAttribute('data-seo-jsonld', 'true');
          document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(data);
      })
      .catch(() => {});
  }, []);

  // Detect the current traffic source from URL params
  const getCurrentSource = () => {
    const utmSource = searchParams.get('utm_source') || '';
    const utmMedium = searchParams.get('utm_medium') || '';
    const campaign = searchParams.get('campaign') || '';
    // Build a source key that identifies the traffic channel
    return [utmSource, utmMedium, campaign].filter(Boolean).join('|') || 'direct';
  };

  // Fetch or restore landing page variant — stable per traffic source
  useEffect(() => {
    const sourceKey = getCurrentSource();
    const STORAGE_KEY = 'lp_variant_assignment';

    // Always fetch from API to get latest test_launched_at for cache validation
    const utmParams = new URLSearchParams();
    const campaign = searchParams.get('campaign') || '';
    if (campaign) utmParams.set('campaign', campaign);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(k => {
      const v = searchParams.get(k);
      if (v) utmParams.set(k, v);
    });

    fetch(`${BACKEND_URL}/api/landing-variants/public/assign?${utmParams.toString()}`)
      .then(r => r.json())
      .then(data => {
        if (data.variant) {
          const serverLaunchedAt = data.test_launched_at || null;

          // Check localStorage — reuse ONLY if same source AND same test phase
          try {
            const cached = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
            if (
              cached &&
              cached.source === sourceKey &&
              cached.variant &&
              cached.testLaunchedAt === serverLaunchedAt
            ) {
              setVariant(cached.variant);
              setVariantReady(true);
              sessionStorage.setItem('lp_variant_id', cached.variant.id);
              sessionStorage.setItem('lp_visitor_id', cached.visitorId);
              return;
            }
          } catch {}

          // Cache miss or stale — use freshly assigned variant
          setVariant(data.variant);
          const vid = `v_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

          // Persist to localStorage with test launch timestamp
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
              source: sourceKey,
              variant: data.variant,
              visitorId: vid,
              assignedAt: Date.now(),
              testLaunchedAt: serverLaunchedAt,
            }));
          } catch {}

          // Store for BookingFlow page
          sessionStorage.setItem('lp_variant_id', data.variant.id);
          sessionStorage.setItem('lp_visitor_id', vid);

          // Track impression
          fetch(`${BACKEND_URL}/api/landing-variants/public/impression`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              variant_id: data.variant.id,
              campaign_id: data.variant.campaign_id,
              visitor_id: vid,
              utm_source: searchParams.get('utm_source') || '',
              utm_medium: searchParams.get('utm_medium') || '',
              utm_campaign: searchParams.get('utm_campaign') || '',
              utm_content: searchParams.get('utm_content') || '',
            })
          }).catch(() => {});
        }
        setVariantReady(true);
      })
      .catch(() => { setVariantReady(true); });
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/services/public/categories`);
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = (channel = 'hero') => {
    if (!onlineBooking) {
      trackClickToCall(channel);
      window.location.assign(`tel:${operatingFlags.salon_phone_e164}`);
      return;
    }
    navigate(buildBookUrl());
  };

  const handleCategoryClick = (category) => {
    if (!onlineBooking) {
      trackClickToCall(`category_${category.slug}`);
      window.location.assign(`tel:${operatingFlags.salon_phone_e164}`);
      return;
    }
    navigate(buildBookUrl(`?category=${category.slug}`));
  };

  // Dynamic values from variant or defaults
  const heroHeadline = variant?.headline;
  const heroSubheadline = variant?.subheadline;
  const heroCta = variant?.cta_text || 'Book Your Appointment';
  // V4 — Programmable top/page-level CTA icon emoji. Reuses the
  // project-native `icon_emoji` pattern (persuasion `BookCtaBlock`).
  // Empty string ⇒ no icon rendered (and no spacing artifact — the
  // render guards on `heroCtaIcon &&`).
  const heroCtaIcon = (variant?.cta_icon_emoji || '').trim();
  const heroImage = variant?.hero_image_url || HERO_IMAGE;
  const accent = variant?.accent_color || '#00A09B';
  const accentHover = accent + 'dd'; // slightly transparent for hover

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf9f7] to-white">
      <SEOHead pageSlug="home" fallbackTitle="Upscale Nails & Spa" fallbackDescription="Premium nail care and spa services in Flower Mound, Texas." />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b" style={{ borderColor: `${accent}1a` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <div className="flex items-center">
              <img 
                src="/images/logo-circular.png" 
                alt="Upscale Nails & Spa" 
                className="h-12 md:h-16 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+19726913618" onClick={() => trackClickToCall('header')} className="hidden md:flex items-center gap-2 text-gray-600 transition-colors" style={{ '--hover-color': accent }}>
                <Phone className="h-4 w-4" />
                <span>(972) 691-3618</span>
              </a>
              <Button 
                onClick={() => handleBookNow('nav')}
                className="text-white px-6 py-2 rounded-full font-medium shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 inline-flex items-center gap-2"
                style={{ backgroundColor: accent }}
                data-testid="nav-book-now-btn"
                data-cascade-cta-id="nav-book"
              >
                {/* E-BCC-30 — nav CTA reads the same page-level CTA source
                    (`heroCta` = variant.cta_text with the canonical fallback)
                    as Hero / Bottom. Independent persuasion `book_cta`
                    remains untouched; contextual nav styling (white-on-accent
                    pill) is preserved. */}
                {onlineBooking ? (
                  <span className="inline-flex items-center gap-1.5">
                    {heroCtaIcon && (
                      <span aria-hidden="true" data-testid="nav-book-now-icon">{heroCtaIcon}</span>
                    )}
                    <span>{heroCta}</span>
                  </span>
                ) : (
                  <><Phone className="h-4 w-4" /> {operatingFlags.salon_phone_display}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      {variantReady ? (
      <section className="relative min-h-[90vh] flex items-center pt-20" data-testid="homepage-hero">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="h-5 w-5" style={{ color: accent }} />
              <span className="font-medium tracking-wide uppercase text-sm" style={{ color: accent }}>Flower Mound, Texas</span>
            </div>
            
            {heroHeadline ? (
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-light text-white mb-6 leading-tight" data-testid="hero-headline">
                {heroHeadline}
              </h1>
            ) : (
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-light text-white mb-6 leading-tight" data-testid="hero-headline">
                Your Moment of
                <span className="block font-serif italic" style={{ color: accent }}>Tranquility</span>
              </h1>
            )}
            
            <p className="text-lg md:text-xl text-gray-300 mb-10 leading-relaxed max-w-xl" data-testid="hero-subheadline">
              {heroSubheadline || 'Experience luxury nail care and spa treatments in a serene, welcoming environment. Book your escape today.'}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => handleBookNow('hero')}
                size="lg"
                className="text-white px-10 py-6 rounded-full text-lg font-medium shadow-2xl transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: accent, boxShadow: `0 25px 50px -12px ${accent}4d` }}
                data-testid="hero-cta-button"
              >
                {onlineBooking
                  ? (
                    <span className="inline-flex items-center gap-2">
                      {heroCtaIcon && (
                        <span aria-hidden="true" className="text-2xl leading-none" data-testid="hero-cta-icon">{heroCtaIcon}</span>
                      )}
                      <span>{heroCta}</span>
                    </span>
                  )
                  : (<><Phone className="mr-2 h-5 w-5" /> Call {operatingFlags.salon_phone_display}</>)
                }
                {onlineBooking && <ChevronRight className="ml-2 h-5 w-5" />}
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-full text-lg backdrop-blur-sm"
              >
                View Services
              </Button>
            </div>

            {/* Quick Info */}
            <div className="flex flex-wrap gap-6 mt-12 text-gray-300 text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" style={{ color: accent }} />
                <span>5810 Long Prairie Rd, Suite 550, Flower Mound</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" style={{ color: accent }} />
                <span data-testid="home-hero-hours">{businessHours.shortLine || "Loading hours…"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 rounded-full animate-pulse" style={{ backgroundColor: accent }} />
          </div>
        </div>
      </section>
      ) : (
        <section className="relative min-h-[90vh] flex items-center justify-center pt-20 bg-[#faf9f7]">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-200" />
            <div className="h-8 w-64 bg-gray-200 rounded" />
            <div className="h-4 w-48 bg-gray-200 rounded" />
          </div>
        </section>
      )}

      {/* Services Section */}
      <section id="services" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-medium tracking-wider uppercase text-sm" style={{ color: accent }}>Our Services</span>
            <h2 className="text-3xl md:text-5xl font-light text-gray-900 mt-4 mb-6">
              Treat Yourself to <span className="font-serif italic">Perfection</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              From classic manicures to luxurious spa pedicures, 
              we offer a full range of services to pamper you.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: accent }}></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((category, index) => {
                const data = CATEGORY_DATA[category.slug] || { icon: '✨', tagline: '' };
                const image = CATEGORY_IMAGES[category.slug] || CATEGORY_IMAGES['manicure'];
                return (
                  <Card 
                    key={category.id}
                    className="group cursor-pointer overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                    onClick={() => handleCategoryClick(category)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={image} 
                        alt={category.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="text-3xl mb-2 block">{data.icon}</span>
                        <h3 className="text-xl font-medium text-white">{category.name}</h3>
                      </div>
                    </div>
                    <div className="p-5 bg-white">
                      <p className="text-gray-600 text-sm mb-3">{category.description || data.tagline}</p>
                      <div className="flex items-center font-medium text-sm group-hover:gap-2 transition-all" style={{ color: accent }}>
                        <span>{onlineBooking ? 'Book Now' : `Call ${operatingFlags.salon_phone_display}`}</span>
                        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="text-center mt-12">
            <Button 
              onClick={() => handleBookNow('full_menu')}
              size="lg"
              className="text-white px-12 py-6 rounded-full text-lg font-medium shadow-xl inline-flex items-center gap-2"
              style={{ backgroundColor: accent }}
            >
              {onlineBooking
                ? 'View Full Menu & Book'
                : (<><Phone className="h-5 w-5" /> Call {operatingFlags.salon_phone_display}</>)
              }
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-[#faf9f7] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-medium tracking-wider uppercase text-sm" style={{ color: accent }}>Why Upscale</span>
              <h2 className="text-3xl md:text-5xl font-light text-gray-900 mt-4 mb-8">
                Where Quality Meets <span className="font-serif italic">Relaxation</span>
              </h2>
              
              <div className="space-y-6">
                {[
                  { icon: Star, title: 'Premium Products', desc: 'We use only high-quality, salon-grade products for the best results.' },
                  { icon: Heart, title: 'Experienced Technicians', desc: 'Our skilled team brings years of expertise to every service.' },
                  { icon: Sparkles, title: 'Relaxing Atmosphere', desc: 'Escape the everyday in our tranquil, spa-inspired environment.' },
                  { icon: Clock, title: 'Convenient Booking', desc: 'Book online in seconds. No phone calls needed.' }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accent}1a` }}>
                      <item.icon className="h-6 w-6" style={{ color: accent }} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={CATEGORY_IMAGES['manicure']} 
                  alt="Beautiful manicure" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center gap-1 mb-2">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="h-5 w-5" style={{ fill: accent, color: accent }} />
                  ))}
                </div>
                <p className="text-gray-700 text-sm italic">
                  "The best nail salon in Flower Mound! Always leave feeling pampered and beautiful."
                </p>
                <p className="font-medium text-sm mt-2" style={{ color: accent }}>— Sarah M.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 relative overflow-hidden" style={{ backgroundColor: accent }} data-testid="homepage-cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-light text-white mb-6">
            Ready for Some <span className="font-serif italic">Self-Care?</span>
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
            Book your appointment in under 60 seconds. 
            Your moment of relaxation awaits.
          </p>
          <Button 
            onClick={() => handleBookNow('bottom')}
            size="lg"
            className="bg-white hover:bg-gray-100 px-12 py-6 rounded-full text-lg font-medium shadow-2xl transition-all duration-300 hover:scale-105 inline-flex items-center gap-2"
            style={{ color: accent }}
            data-testid="homepage-bottom-cta"
          >
            {onlineBooking
              ? heroCta
              : (<><Phone className="h-5 w-5" /> Call {operatingFlags.salon_phone_display}</>)
            }
            {onlineBooking && <ChevronRight className="ml-2 h-5 w-5" />}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <img 
                src="/images/logo-linear.png" 
                alt="Upscale Nails & Spa" 
                className="h-16 w-auto mb-6 brightness-0 invert"
              />
              <p className="text-gray-400 mb-6 max-w-md">
                Premium nail care and spa services in the heart of Flower Mound. 
                Where relaxation meets excellence.
              </p>
              <div className="flex gap-4">
                <a href="https://facebook.com" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center transition-colors" style={{ '--tw-hover-bg': accent }}>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/></svg>
                </a>
                <a href="https://instagram.com" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center transition-colors" style={{ '--tw-hover-bg': accent }}>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-4">Contact</h4>
              <div className="space-y-3 text-gray-400 text-sm">
                <p>5810 Long Prairie Road</p>
                <p>Suite 550</p>
                <p>Flower Mound, TX 75028</p>
                <a href="tel:+19726913618" onClick={() => trackClickToCall('footer')} className="block" style={{ color: accent }}>(972) 691-3618</a>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-4">Hours</h4>
              <div className="space-y-2 text-gray-400 text-sm" data-testid="home-footer-hours">
                {businessHours.byDayLines.length === 0 && <p>Loading…</p>}
                {businessHours.byDayLines.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} Upscale Nails & Spa. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-gray-500 text-sm items-center">
              <PublicPrivacyLinks tone="dark" />
              <Link to="/login" className="transition-colors" style={{ '--hover-color': accent }}>Staff Portal</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
